import { getHighestNumber } from '@/util/number';
import IconButton from '@/components/common/IconButton';
import { useApplication } from '@/hooks/useApplication';
import { Semaphore } from '@/types/semaphores';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { Stack, TextField } from '@mui/material';
import { FC, useState } from 'react';

type SemaphoreListItemEditProps = {
  semaphore?: Semaphore;
  onClose: (isEmpty?: boolean) => void;
  onSave: (newSemaphore: Semaphore) => void;
};

const SemaphoreListItemEdit: FC<SemaphoreListItemEditProps> = (props) => {
  const { application } = useApplication();

  const [semaphoreName, setSemaphoreName] = useState(props.semaphore?.name || '');
  const [semaphoreValue, setSemaphoreValue] = useState<number | undefined>(props.semaphore?.value || 1);
  const isError = semaphoreName.startsWith('_debug');
  const valid: boolean = !!semaphoreName && !!semaphoreValue && !isError;

  const handleClose = (isEmpty?: boolean) => {
    setSemaphoreName('');
    setSemaphoreValue(1);
    props.onClose(isEmpty);
  };

  const generateNewId = (semaphores: Semaphore[]): number => getHighestNumber(semaphores, 'id') + 1;

  return (
    <Stack
      direction='row'
      gap={1}
      pt={1}
      width='100%'
      alignItems='flex-start'
      alignContent='flex-start'
      bgcolor='action.focus'
      borderRadius={2}
      p={1}
    >
      {props.semaphore?.id && <code>{props.semaphore.id}</code>}
      <Stack gap={1} flexDirection={'row'} width='100%'>
        <TextField
          label='Semaphore Name'
          value={semaphoreName}
          onChange={(event) => setSemaphoreName(event.currentTarget.value.trimStart())}
          fullWidth
          size='small'
          autoFocus
          error={isError}
          helperText={isError ? "Names cannot start with '_debug'" : ''}
          required
          InputProps={{ autoComplete: 'off' }}
        />
        <TextField
          label='Initial Value'
          value={semaphoreValue}
          onChange={(event) => {
            const parse = parseInt(event.currentTarget.value);
            setSemaphoreValue(isNaN(parse) ? undefined : Math.abs(parse));
          }}
          fullWidth
          size='small'
          required
          InputProps={{ autoComplete: 'off' }}
        />
      </Stack>
      <IconButton
        tooltip={valid ? 'Save' : undefined}
        disabled={!valid}
        onClick={() => {
          if (!valid) return;
          const newSemaphore: Semaphore = {
            id: props.semaphore?.id || generateNewId(application.semaphores || []),
            name: semaphoreName,
            value: semaphoreValue || 0
          };
          props.onSave(newSemaphore);
          handleClose();
        }}
      >
        <SaveIcon />
      </IconButton>
      <IconButton tooltip='Cancel' onClick={() => handleClose(!props.semaphore?.name)}>
        <CancelIcon />
      </IconButton>
    </Stack>
  );
};

export default SemaphoreListItemEdit;
